import React, { useCallback, useEffect } from 'react';
import Card from 'components/inputs/Card';
import { FETCH_DEALER_CREDIT_TRANSACTIONS } from 'actions/dealerEntitlement';
import { useDispatch, useSelector } from 'react-redux';
import * as _ from 'lodash';
import { useParams } from 'react-router-dom';
import ReactPagination from 'components/general/ReactPagination';
import moment from 'moment';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';

/**
 * Dealer energy-credit movements for one customer.
 *
 * Its own tab rather than merged into the wallet Transactions list: energy
 * credit is a per-vehicle balance separate from the wallet, so showing both in
 * one table would put two unrelated running balances in the same column. One
 * row per ledger movement (released / used / expired / reversed), tagged with
 * the vehicle and dealer it belongs to.
 */
const fmtDate = (d) => (d ? moment(d).format('DD/MM/YYYY HH:mm') : '—');
// Paise matter here: 10% of ₹81 is ₹8.10 and must not be rounded away.
const money = (v) => (_.isNil(v) ? '—' : `₹${Number(v).toFixed(2)}`);
const value = (v) => (_.isNil(v) || v === '' ? '—' : v);

const EnergyCreditTransaction = () => {
  const dispatch = useDispatch();
  const { customerId } = useParams();

  const transactions = useSelector((state) => state.dealerEntitlement.transactions);
  const stats = useSelector((state) => state.dealerEntitlement.transactionStats);
  const isLoading = useSelector((state) => state.dealerEntitlement.isLoading);
  const page = useSelector((state) => state.dealerEntitlement.txPage);
  const limit = useSelector((state) => state.dealerEntitlement.txLimit);
  const totalPages = useSelector((state) => state.dealerEntitlement.txTotalPages);
  const totalData = useSelector((state) => state.dealerEntitlement.txTotal);

  const getTransactions = useCallback(
    (data = {}) => {
      dispatch({ type: FETCH_DEALER_CREDIT_TRANSACTIONS.REQUEST, payload: { ...data, user: customerId } });
    },
    [dispatch, customerId]
  );

  useEffect(() => {
    getTransactions();
  }, [getTransactions]);

  const cards = [
    { key: 'balance', label: 'Available Balance', value: money(_.get(stats, 'balance.outstanding')) },
    { key: 'lifetime', label: 'Lifetime Commitment', value: money(_.get(stats, 'balance.lifetime_value')) },
    { key: 'vehicles', label: 'Active Vehicles', value: _.get(stats, 'balance.active_ledgers', 0) },
    { key: 'thisYear', label: 'This Year Released', value: money(_.get(stats, 'this_year.released')) },
    { key: 'thisYearUsed', label: 'This Year Used', value: money(_.get(stats, 'this_year.used')) },
    { key: 'used', label: 'Total Used', value: money(_.get(stats, 'movement.used')) },
  ];

  return (
    <SkeletonTheme baseColor="#1b2a38" highlightColor="#092235">
      <div className="data-report--main energy-credits__main">
        <Card>
          <div className="data-report__inner">
            <div className="ec__stats">
              {_.map(cards, (card) => (
                <div className="ec__stat" key={card.key}>
                  <div className="ec__stat-label">{card.label}</div>
                  <div className="ec__stat-value">{isLoading ? <Skeleton height={15} width={70} /> : card.value}</div>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card>
          <div className="table-responsive">
            <table className="record-list-table ec__statement-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Year</th>
                  <th>Vehicle</th>
                  <th>Dealer</th>
                  <th>Station</th>
                  <th>Booking ID</th>
                  <th>Invoice No</th>
                  <th>Balance After</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={10} className="border-0">
                      <Skeleton height={15} />
                    </td>
                  </tr>
                ) : _.isEmpty(transactions) ? (
                  <tr>
                    <td colSpan={10} className="border-0">
                      <div className="empty-data-block">No energy credit transactions</div>
                    </td>
                  </tr>
                ) : (
                  _.map(transactions, (item, index) => (
                    <tr key={`energy-credit-tx-${index}`}>
                      <td>{fmtDate(_.get(item, 'at'))}</td>
                      <td>{_.startCase(_.get(item, 'reason', _.get(item, 'type', '')))}</td>
                      <td>{money(_.get(item, 'amount'))}</td>
                      <td>{_.get(item, 'year', '—')}</td>
                      <td>{_.get(item, 'entitlement.rto_no', '—')}</td>
                      <td>{_.get(item, 'dealer.name', '—')}</td>
                      <td>{_.get(item, 'station.name', '—')}</td>
                      <td className="ec__id">{value(_.get(item, 'booking'))}</td>
                      <td className="ec__id">{value(_.get(item, 'invoice_no'))}</td>
                      <td>{money(_.get(item, 'balance_after'))}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!isLoading && !_.isEmpty(transactions) && (
            <ReactPagination
              currentPage={page}
              limit={limit}
              total={totalData}
              totalPages={totalPages}
              handlePageClick={(pageVal) => getTransactions({ page: pageVal.selected + 1 })}
              marginPagesDisplayed={1}
              showDataCount={true}
            />
          )}
        </Card>
      </div>
    </SkeletonTheme>
  );
};

export default EnergyCreditTransaction;
